import {
  Injectable,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  inject,
  signal,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { APP_CONSTANTS } from "../../../core/constants/app.constants";
import type { IceServerConfig } from "../models/video.models";

@Injectable()
export class VideoWebRtcService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);

  private pc: RTCPeerConnection | null = null;
  private localMedia: MediaStream | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private remoteDescSet = false;

  readonly localStream = signal<MediaStream | null>(null);
  readonly remoteStream = signal<MediaStream | null>(null);
  readonly connectionState = signal<RTCIceConnectionState>("new");
  readonly isMuted = signal(false);
  readonly isCameraOff = signal(false);

  // Page component sets this to route outgoing ICE candidates to the socket
  onIceCandidate?: (candidate: RTCIceCandidateInit) => void;

  async requestMedia(): Promise<"granted" | "denied"> {
    if (!isPlatformBrowser(this.platformId)) return "denied";
    if (this.localMedia?.active) return "granted";
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: APP_CONSTANTS.media.video,
        audio: APP_CONSTANTS.media.audio,
      });
      this.localMedia = stream;
      this.localStream.set(stream);
      return "granted";
    } catch {
      return "denied";
    }
  }

  hasRemoteDescription(): boolean {
    return this.remoteDescSet;
  }

  createPeerConnection(iceServers: IceServerConfig[]): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.closePeerConnection();
    this.pc = new RTCPeerConnection({
      iceServers: iceServers as RTCIceServer[],
    });
    this.remoteDescSet = false;

    this.localMedia
      ?.getTracks()
      .forEach((track) => this.pc!.addTrack(track, this.localMedia!));

    // Run outside Angular zone — ICE packets fire very frequently during negotiation.
    // Only re-enter the zone when updating signals.
    this.zone.runOutsideAngular(() => {
      this.pc!.onicecandidate = ({ candidate }) => {
        if (candidate) this.onIceCandidate?.(candidate.toJSON());
      };

      this.pc!.ontrack = (event) => {
        this.zone.run(() => this.remoteStream.set(event.streams[0] ?? null));
      };

      this.pc!.oniceconnectionstatechange = () => {
        this.zone.run(() => {
          if (this.pc) this.connectionState.set(this.pc.iceConnectionState);
        });
      };
    });
  }

  async createOffer(): Promise<RTCSessionDescriptionInit> {
    const offer = await this.pc!.createOffer();
    await this.pc!.setLocalDescription(offer);
    return offer;
  }

  async handleOffer(
    sdp: RTCSessionDescriptionInit,
  ): Promise<RTCSessionDescriptionInit> {
    await this.pc!.setRemoteDescription(new RTCSessionDescription(sdp));
    this.remoteDescSet = true;
    await this.flushPendingCandidates();
    const answer = await this.pc!.createAnswer();
    await this.pc!.setLocalDescription(answer);
    return answer;
  }

  async handleAnswer(sdp: RTCSessionDescriptionInit): Promise<void> {
    await this.pc!.setRemoteDescription(new RTCSessionDescription(sdp));
    this.remoteDescSet = true;
    await this.flushPendingCandidates();
  }

  async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.pc) return;
    // Queue candidates arriving before remote description is set (common on fast networks)
    if (!this.remoteDescSet) {
      this.pendingCandidates.push(candidate);
      return;
    }
    await this.pc
      .addIceCandidate(new RTCIceCandidate(candidate))
      .catch(() => {});
  }

  toggleMute(): void {
    const track = this.localMedia?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    this.isMuted.set(!track.enabled);
  }

  toggleCamera(): void {
    const track = this.localMedia?.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    this.isCameraOff.set(!track.enabled);
  }

  // Call between conversations — closes WebRTC but keeps camera/mic alive
  resetForNewCall(): void {
    this.closePeerConnection();
    this.remoteStream.set(null);
    this.connectionState.set("new");
  }

  // Full cleanup on page leave — stops camera LED
  cleanup(): void {
    this.closePeerConnection();
    this.localMedia?.getTracks().forEach((t) => t.stop());
    this.localMedia = null;
    this.localStream.set(null);
    this.remoteStream.set(null);
    this.connectionState.set("new");
    this.isMuted.set(false);
    this.isCameraOff.set(false);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private closePeerConnection(): void {
    this.pc?.close();
    this.pc = null;
    this.pendingCandidates = [];
    this.remoteDescSet = false;
  }

  private async flushPendingCandidates(): Promise<void> {
    const toFlush = this.pendingCandidates.splice(0);
    for (const c of toFlush) {
      await this.pc?.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
    }
  }
}
